import CreditCard from "../CreditCard";
import cn from "../../lib/classnames";
import React from "react";
import styles from "./Swipable.module.css";
import {Card as CardOption} from "./cardCategories";

// based off https://stackoverflow.com/questions/40463173/swipe-effect-in-react-js

const OPTION_WIDTH = 268;
const SPACE_BETWEEN_OPTIONS = 25;
const INTERVAL_MILLISECONDS = 33;

type State = {
    swipedLeftWithVisibleOptionChange: boolean;
    swipedRightWithVisibleOptionChange: boolean;
    optionIndex: number;
    currentTextColor: string;
    leftTextColor: string;
    rightTextColor: string;
    offset: number;
    originalOffset: number;
    velocity: number;
    timeOfLastSwipeEvent: number;
    touchStartX: number;
    prevTouchX: number;
    swiping: boolean;
    intervalId: number | undefined;
};

type Props = {
    onSwipeLeft: () => void;
    onSwipeRight: () => void;
    swipeBoundary: number;
    options: CardOption[];
    optionIndex: number;
    currentTextColor: string;
    leftTextColor: string;
    rightTextColor: string;
    name?: string;
};

const strings = {
    yourNameHere: "Your Name Here",
};

export default class Swipable extends React.Component<Props, State> {
    static defaultProps = {
        swipeBoundary: 200,
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            swipedLeftWithVisibleOptionChange: false,
            swipedRightWithVisibleOptionChange: false,
            optionIndex: props.optionIndex,
            currentTextColor: props.currentTextColor,
            leftTextColor: props.leftTextColor,
            rightTextColor: props.rightTextColor,
            offset: 0,
            originalOffset: 0,
            velocity: 0,
            timeOfLastSwipeEvent: 0,
            touchStartX: 0,
            prevTouchX: 0,
            swiping: false,
            intervalId: undefined,
        };
    }

    _mounted = false;

    componentDidMount() {
        this._mounted = true;
    }

    componentDidUpdate(prevProps: Props) {
        // in case current option changes through non-swipe interaction (ex: clicking a button in parent component)
        if (
            (prevProps.optionIndex !== this.props.optionIndex &&
                this.state.optionIndex !== this.props.optionIndex) ||
            (prevProps.currentTextColor !== this.props.currentTextColor &&
                this.state.currentTextColor !== this.props.currentTextColor) ||
            (prevProps.leftTextColor !== this.props.leftTextColor &&
                this.state.leftTextColor !== this.props.leftTextColor) ||
            (prevProps.rightTextColor !== this.props.rightTextColor &&
                this.state.rightTextColor !== this.props.rightTextColor)
        ) {
            this.setState({
                optionIndex: this.props.optionIndex,
                currentTextColor: this.props.currentTextColor,
                leftTextColor: this.props.leftTextColor,
                rightTextColor: this.props.rightTextColor,
            });
        }
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    // swiped far enough one direction to trigger option change
    handleVisibleOptionChange = (swipingLeft: boolean) => {
        if (this.state.intervalId !== undefined) {
            window.clearInterval(this.state.intervalId);
        }

        let newOptionIndex = 0;
        let newOffset = 0;
        let newCurrentColor = this.props.currentTextColor;
        let newLeftColor = this.props.leftTextColor;
        let newRightColor = this.props.rightTextColor;

        // update option index to next (left swipe) or prev (right swipe) option
        // update offset so visually the cards are positioned the same even though the option index changed
        if (swipingLeft) {
            newOptionIndex =
                this.state.optionIndex === this.props.options.length - 1
                    ? 0
                    : this.state.optionIndex + 1;
            newOffset = OPTION_WIDTH + SPACE_BETWEEN_OPTIONS + this.state.offset;
            newCurrentColor = this.props.rightTextColor;
            newLeftColor = this.props.currentTextColor;
        } else {
            newOptionIndex =
                this.state.optionIndex === 0
                    ? this.props.options.length - 1
                    : this.state.optionIndex - 1;
            newOffset = -OPTION_WIDTH - SPACE_BETWEEN_OPTIONS + this.state.offset;
            newCurrentColor = this.props.leftTextColor;
            newRightColor = this.props.currentTextColor;
        }

        if (this._mounted) {
            this.setState({
                swipedLeftWithVisibleOptionChange: swipingLeft,
                swipedRightWithVisibleOptionChange: !swipingLeft,
                optionIndex: newOptionIndex,
                currentTextColor: newCurrentColor,
                rightTextColor: newRightColor,
                leftTextColor: newLeftColor,
                offset: newOffset,
                velocity: 10,
                originalOffset: 0,
                timeOfLastSwipeEvent: 0,
                touchStartX: 0,
                prevTouchX: 0,
                swiping: false,
                // start animating slide to 0
                // use negated swipe direction since option index changed, so animation looks similar to after swiping partially in opposite direction
                intervalId: window.setInterval(
                    this.animateSlidingToZero,
                    INTERVAL_MILLISECONDS,
                    !swipingLeft,
                ),
            });
        }
    };

    animateSlidingToZero = (wasSwipingLeft: boolean) => {
        if (this.state.swiping) {
            return;
        }

        if (wasSwipingLeft && this.state.offset < -0.01) {
            // was swiping left and option is not in its original location
            const velocity = this.state.velocity + 10 * 0.15;
            const offset = this.state.offset + velocity;
            if (offset < -this.props.swipeBoundary) {
                // option had enough velocity on release that it slid past the swipe boundary
                this.handleVisibleOptionChange(true);
            } else {
                this.setState({velocity, offset});
            }
        } else if (!wasSwipingLeft && this.state.offset > 0.01) {
            // was swiping right and option is not in its original location
            const velocity = this.state.velocity + 10 * 0.15;
            const offset = this.state.offset - velocity;
            if (offset > this.props.swipeBoundary) {
                // option had enough velocity on release that it slid past the swipe boundary
                this.handleVisibleOptionChange(false);
            } else {
                this.setState({velocity, offset});
            }
        } else {
            // call props on-option-change function once new option slides to 0 offset
            // calling after swipe animation finishes to avoid visible side effects of changing option mid-swipe
            if (this.state.swipedLeftWithVisibleOptionChange) {
                this.props.onSwipeLeft();
            } else if (this.state.swipedRightWithVisibleOptionChange) {
                this.props.onSwipeRight();
            }

            window.clearInterval(this.state.intervalId);
            this.setState({
                swipedLeftWithVisibleOptionChange: false,
                swipedRightWithVisibleOptionChange: false,
                velocity: 0,
                offset: 0,
                originalOffset: 0,
                intervalId: undefined,
            });
        }
    };

    handleSwipeStart = (clientX: number) => {
        if (this.state.intervalId !== undefined) {
            window.clearInterval(this.state.intervalId);
        }

        this.setState({
            originalOffset: this.state.offset,
            velocity: 0,
            timeOfLastSwipeEvent: Date.now(),
            touchStartX: clientX,
            swiping: true,
            intervalId: undefined,
        });
    };

    handleSwipeMove = (clientX: number) => {
        if (!this.state.swiping) {
            return;
        }

        const currentTime = Date.now();
        const elapsed = currentTime - this.state.timeOfLastSwipeEvent;
        let velocity = 0;
        const deltaX = clientX - this.state.touchStartX + this.state.originalOffset;

        if (deltaX < 0) {
            velocity = (20 * (clientX - this.state.prevTouchX)) / elapsed;
        } else if (deltaX > 0) {
            velocity = (20 * (this.state.prevTouchX - clientX)) / elapsed;
        }

        if (deltaX < -this.props.swipeBoundary) {
            // swipe left far enough to trigger option change
            this.handleVisibleOptionChange(true);
        } else if (deltaX > this.props.swipeBoundary) {
            // swipe right far enough to trigger option change
            this.handleVisibleOptionChange(false);
        } else {
            this.setState({
                offset: deltaX,
                velocity,
                timeOfLastSwipeEvent: currentTime,
                prevTouchX: clientX,
            });
        }
    };

    handleSwipeEnd = () => {
        if (!this.state.swiping) {
            return;
        }

        const wasSwipingLeft = this.state.offset < 0;

        this.setState({
            touchStartX: 0,
            swiping: false,
            intervalId: window.setInterval(
                this.animateSlidingToZero,
                INTERVAL_MILLISECONDS,
                wasSwipingLeft,
            ),
        });
    };

    handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        e.preventDefault();
        this.handleSwipeStart(e.targetTouches[0].clientX);
    };

    handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        this.handleSwipeMove(e.targetTouches[0].clientX);
    };

    handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        this.handleSwipeStart(e.clientX);
    };

    handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        this.handleSwipeMove(e.clientX);
    };

    render() {
        return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <div
                className={styles.container}
                role="img"
                onTouchStart={this.handleTouchStart}
                onTouchMove={this.handleTouchMove}
                onTouchEnd={this.handleSwipeEnd}
                onMouseDown={this.handleMouseDown}
                onMouseMove={this.handleMouseMove}
                onMouseUp={this.handleSwipeEnd}
                onMouseLeave={this.handleSwipeEnd}
            >
                {this.props.options.map((o, i) => {
                    const isCurrentCard = i === this.state.optionIndex;
                    const isNextCardLeft =
                        i + 1 === this.state.optionIndex ||
                        (this.state.optionIndex === 0 && i === this.props.options.length - 1);
                    const isNextCardRight =
                        i - 1 === this.state.optionIndex ||
                        (this.state.optionIndex === this.props.options.length - 1 && i === 0);

                    if (!isCurrentCard && !isNextCardLeft && !isNextCardRight) {
                        return null;
                    }

                    let textColor = this.state.currentTextColor;
                    let containerStyle: React.CSSProperties = {};
                    const cardValue = this.props.options[i].value;
                    if (isCurrentCard) {
                        // position the current option to the current offset
                        containerStyle = {left: `${this.state.offset}px`};
                    } else if (isNextCardLeft) {
                        // position the previous option to the left of the current option
                        containerStyle = {
                            left: `calc(-100% - ${SPACE_BETWEEN_OPTIONS}px + ${this.state.offset}px)`,
                        };
                        textColor = this.state.leftTextColor;
                    } else if (isNextCardRight) {
                        // position the next option to the right of the current option
                        containerStyle = {
                            left: `calc(100% + ${SPACE_BETWEEN_OPTIONS}px + ${this.state.offset}px)`,
                        };
                        textColor = this.state.rightTextColor;
                    }

                    const cardText = this.props.name?.length
                        ? this.props.name
                        : strings.yourNameHere;

                    return (
                        <div
                            key={o.value}
                            className={cn(
                                styles.swipable,
                                !isCurrentCard && !isNextCardLeft && !isNextCardRight
                                    ? styles.offscreen
                                    : undefined,
                            )}
                            style={containerStyle}
                        >
                            <CreditCard
                                imageAlt={o.name}
                                imageSources={o.images}
                                text={cardText}
                                textColor={textColor}
                                cardValue={cardValue}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }
}
